/* eslint-disable jsx-a11y/anchor-is-valid */
import clsx from 'clsx'
import {FC, useMemo, useState} from 'react'
import {usersAPI} from '../../../../../../services/UsersService'
import {KTSVG} from '../../../../../../template/helpers'
import {formatDateByZone} from '../../../../../../template/helpers/FormatDateZoneHelpers'
import {IAppsModel} from '../../../../../../types/apps/AppsModel'
import {clipboard} from '../../../../../../template/helpers/Clipboard'
import {Link, useLocation} from 'react-router-dom'
import {DeleteAppModal} from '../../../modals/DeleteAppModal'
import {useIntl} from 'react-intl'
import {activeUsersCount, totalUsersCount} from '../../../../../../template/helpers/TotalUsers'
import {CRMTitle} from '../../../../../../template/helpers/components/CRMTitle'

type Props = {
  app: IAppsModel
}

const PanelAppItemHeaderData: FC<Props> = ({app}) => {
  const intl = useIntl()
  const location = useLocation()
  const [buffer, setBuffer] = useState(false)
  const {data: currentUser} = usersAPI.useFetchGetMeQuery()
  const [show, setShow] = useState(false)
  const [linkCDR] = useState<string>(location.pathname.includes('/management') ? '/management/cdr' : '/cdr')
  const [linkCSR] = useState<string>(location.pathname.includes('/management') ? '/management/csr' : '/csr')

  const copyBufer = (copyData: string) => {
    setBuffer(true)
    clipboard(copyData)
    setTimeout(() => {
      setBuffer(false)
    }, 600)
  }

  function dateTime(date: string) {
    return formatDateByZone(date, currentUser?.language || 'en', currentUser?.time_zone || 'UTC')
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const activeUsers = useMemo(() => activeUsersCount(app), [app])
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const totalUsers = useMemo(() => totalUsersCount(app), [app])

  const handleClose = () => setShow(false)
  const handleShow = () => setShow(true)

  return (
    <>
      <div className='separator'></div>
      <div className='flex-equal'>
        <div className='d-flex fw-bolder text-gray-600 mt-5 mb-1 small text-uppercase'>
          {intl.formatMessage({id: 'APPS.ID'})}
        </div>
        <div className='d-flex flex-stack'>
          <div className='d-flex'>
            <span className={clsx('badge', {'badge-light-info': !buffer}, {'badge-light-success': buffer})}>
              {app?.id}
            </span>
          </div>
          <div className='d-flex text-end'>
            <button
              onClick={() => copyBufer(app?.id!)}
              className={clsx('btn btn-link text-gray-900 pt-0 pb-0', {'text-hover-primary': !buffer})}
            >
              {!buffer ? (
                <KTSVG path='/media/icons/duotune/general/gen054.svg' className='svg-icon-gray-900 svg-icon m-0' />
              ) : (
                <KTSVG path='/media/icons/duotune/arrows/arr012.svg' className='svg-icon-success m-0' />
              )}
            </button>
          </div>
        </div>
        <div className='d-flex fw-bolder text-gray-600 mt-5 mb-1 small text-uppercase'>
          <CRMTitle crm={app.crm} />
        </div>

        <div className='d-flex flex-stack'>
          <div className='d-flex col-8 fw-bolder text-gray-800 me-2'>{app?.crm_domain}</div>
          {app.crm !== 'ONEC' && (
            <a href={'https://' + app?.crm_domain} target='_blank' rel='noreferrer' className='ms-1 col-2 text-end'>
              <KTSVG
                path='/media/icons/duotune/arrows/arr095.svg'
                className='svg-icon svg-icon-gray-900 text-hover-primary'
              />
            </a>
          )}
        </div>
        <div className='d-flex flex-stack mt-5 mb-1 text-uppercase'>
          <div className='d-flex fw-bolder text-gray-600 small'>
            {intl.formatMessage({id: 'APPS.CRM'})} / {intl.formatMessage({id: 'APPS.PBX'})}
          </div>
          <div className='d-flex text-right align-items-center'>
            <div className='badge badge-light-primary me-1'>{app.crm === 'ONEC' ? '1C' : app.crm}</div>
            <KTSVG path='/media/icons/duotune/abstract/abs022.svg' className='svg-icon-gray-600 svg-icon-1x' />
            <div className='badge badge-light-warning ms-1 me-1'>{app.pbx}</div>
          </div>
        </div>
        <div className='d-flex flex-stack mt-5 mb-1'>
          <div className='d-flex fw-bolder text-gray-600 small text-uppercase'>
            {intl.formatMessage({id: 'APPS.EXTENSIONS'})}
          </div>
          {app.extensions && (
            <div className='d-flex align-items-center ms-5'>
              <Link to={`${linkCDR}/${app.id}/`} className='me-1'>
                {app.extensions.cdr ? (
                  <span className='badge badge-light-primary cursor-pointer'>
                    <KTSVG path='/media/icons/duotune/files/fil002.svg' className='svg-icon-primary' />
                  </span>
                ) : (
                  <span className='badge badge-light-primary cursor-pointer'>
                    <KTSVG path='/media/icons/duotune/files/fil002.svg' className='svg-icon-danger' />
                  </span>
                )}
              </Link>
              <Link to={`${linkCSR}/${app.id}/`}>
                {app.extensions.cdr ? (
                  <span className='badge badge-light-primary cursor-pointer'>
                    <KTSVG path='/media/icons/duotune/graphs/gra001.svg' className='svg-icon-primary' />
                  </span>
                ) : (
                  <span className='badge badge-light-primary cursor-pointer'>
                    <KTSVG path='/media/icons/duotune/graphs/gra001.svg' className='svg-icon-danger' />
                  </span>
                )}
              </Link>
            </div>
          )}
        </div>
        <div className='d-flex flex-stack mt-5 mb-1'>
          <div className='d-flex fw-bolder text-gray-600 small text-uppercase'>
            {intl.formatMessage({id: 'APPS.VERSION'})}
          </div>
          <span className='d-flex text-right badge badge-light-info'>{app.version}</span>
        </div>
        <div className='d-flex flex-stack mt-5 mb-1 text-uppercase'>
          <div className='d-flex fw-bolder text-gray-600 small text-uppercase'>
            {intl.formatMessage({id: 'APPS.REGION'})}
          </div>
          <div className='d-flex text-right'>
            {app.is_auto_mode && <span className='badge badge-light-danger small me-2'>A</span>}
            <span className='badge badge-light-info'>{app.region}</span>
          </div>
        </div>
        <div className='d-flex flex-stack mt-5 mb-1'>
          <div className='fw-bolder text-gray-600 small text-uppercase'>
            {intl.formatMessage({id: 'APPS.CREATED'})}
            <span className='d-flex fw-bolder text-gray-800'>{dateTime(app.created_on)}</span>
          </div>
        </div>
      </div>
      <div className='separator mb-5 mt-5'></div>
      <div className='flex-equal'>
        <div className='d-flex flex-stack mt-5 mb-1 text-uppercase'>
          <div className='d-flex fw-bolder text-gray-600 small text-uppercase'>
            {intl.formatMessage({id: 'APPS.LICENSE.TYPE'})}
          </div>
          {app.licenses.is_commercial ? (
            <div className='d-flex text-right badge badge-success'>Commercial </div>
          ) : (
            <div className='d-flex text-right badge badge-warning'>Trial </div>
          )}
        </div>
        <div className='d-flex flex-stack mt-5 mb-1 text-uppercase'>
          <div className='d-flex fw-bolder text-gray-600 small text-uppercase'>
            {intl.formatMessage({id: 'APPS.LICENSE.STATUS'})}
          </div>
          {app.licenses.is_active ? (
            <div className='d-flex text-right badge badge-success'>ACTIVE</div>
          ) : (
            <div className='d-flex text-right badge badge-danger'>EXPIRED</div>
          )}
        </div>
        {app.crm !== 'ROISTAT' && (
          <>
            <div className='d-flex flex-stack mt-5 mb-1 text-uppercase'>
              <div className='d-flex fw-bolder text-gray-600 small text-uppercase'>
                {intl.formatMessage({id: 'APPS.LICENSE.TOTAL'})}
              </div>
              <div className='d-flex text-right fw-bolder text-gray-800'>{app.licenses.maxusers}</div>
            </div>
            <div className='d-flex flex-stack mt-5 mb-1 text-uppercase'>
              {activeUsers > app.licenses.maxusers ? (
                <>
                  <div className='d-flex fw-bolder text-danger small text-uppercase'>
                    {intl.formatMessage({id: 'APPS.LICENSE.ACTIVE'})} <br /> (
                    {intl.formatMessage({id: 'APPS.LICENSE.ERROR'})})
                  </div>
                  <div className='d-flex text-right fw-bolder text-danger'>{activeUsers}</div>
                </>
              ) : (
                <>
                  <div className='d-flex fw-bolder text-gray-600 small text-uppercase'>
                    {intl.formatMessage({id: 'APPS.LICENSE.ACTIVE'})}
                  </div>
                  <div className='d-flex text-right fw-bolder text-gray-800'>{activeUsers}</div>
                </>
              )}
            </div>
            <div className='d-flex flex-stack mt-5 mb-1 text-uppercase'>
              <div className='d-flex fw-bolder text-gray-600 small text-uppercase'>
                {intl.formatMessage({id: 'APPS.LICENSE.TOTAL.USERS'})}
              </div>
              <div className='d-flex text-right fw-bolder text-gray-800'>{totalUsers}</div>
            </div>
          </>
        )}

        <div className='d-flex flex-stack mt-5 mb-1'>
          <div
            className={clsx(
              'fw-bolder small text-uppercase',
              {'text-gray-600': app.licenses.is_active},
              {'text-danger': !app.licenses.is_active}
            )}
          >
            {intl.formatMessage({id: 'APPS.LICENSE.END'})}
            <span
              className={clsx(
                'd-flex fw-bolder',
                {'text-gray-800': app.licenses.is_active},
                {'text-danger': !app.licenses.is_active}
              )}
            >
              {dateTime(app.licenses.license_end)}
            </span>
          </div>
        </div>
      </div>
      {currentUser?.role !== 'CLIENT' && (
        <>
          <div className='separator mb-5 mt-5'></div>
          <div className='flex-equal'>
            <div className='d-flex fw-bolder text-gray-600 mt-5 mb-1 small text-uppercase'>
              {intl.formatMessage({id: 'APPS.OWNER.INFO'})}
            </div>
            <div className='d-flex flex-stack'>
              <div className='d-flex col-8 text-gray-800 me-2'>{app?.user.fullname}</div>
              <Link to={`/management/users/${app.user.id}/`} target='_blank'>
                <KTSVG
                  path='/media/icons/duotune/arrows/arr095.svg'
                  className='svg-icon svg-icon-gray-900 text-hover-primary'
                />
              </Link>
            </div>
            <div className='d-flex flex-stack'>
              <div className='d-flex col-8 text-gray-800'>{app?.user.company}</div>
            </div>
            {app.user.tax_id && (
              <div className=''>
                {intl.formatMessage({id: 'PROFILE.DETAIL.TAXID'})}: {app.user.tax_id}
              </div>
            )}
            <div className='d-flex flex-stack'>
              <div className='d-flex col-8 text-gray-800'>{app?.user.email}</div>
            </div>
            <div className='d-flex flex-stack'>
              <div className='d-flex col-8 text-gray-800'>{app?.user.phone}</div>
            </div>
          </div>
        </>
      )}
      {currentUser?.role === 'ADMIN' ? (
        <>
          <div className='separator mb-5 mt-5' />
          <div className='d-flex flex-column bd-highlight'>
            <button className='btn btn-secondary btn-sm text-danger' onClick={handleShow}>
              {intl.formatMessage({id: 'APPS.DELETE.BUTTON'})}
            </button>
          </div>
          <DeleteAppModal appId={app.id} show={show} handleClose={handleClose} />
        </>
      ) : (
        <>
          {currentUser?.role === 'CLIENT' && !app.licenses.is_commercial && (
            <>
              <div className='separator mb-5 mt-5' />
              <div className='d-flex flex-column bd-highlight'>
                <button className='btn btn-secondary btn-sm text-danger' onClick={handleShow}>
                  {intl.formatMessage({id: 'APPS.DELETE.BUTTON'})}
                </button>
              </div>
              <DeleteAppModal appId={app.id} show={show} handleClose={handleClose} />
            </>
          )}
        </>
      )}
    </>
  )
}

export {PanelAppItemHeaderData}
